// §35.4 canonical application error interface
export interface ApplicationError {
  code: string;
  message: string;
  retryable: boolean;
  httpStatus?: number;
  details?: Record<string, unknown>;
  cause?: unknown;
}

/** Runtime error class that carries a stable code and HTTP status. */
export class AppError extends Error implements ApplicationError {
  public readonly code: string;
  public readonly retryable: boolean;
  public readonly httpStatus: number;
  public readonly details?: Record<string, unknown>;
  public override readonly cause?: unknown;

  public constructor(opts: ApplicationError) {
    super(opts.message);
    this.name = "AppError";
    this.code = opts.code;
    this.retryable = opts.retryable;
    this.httpStatus = opts.httpStatus ?? 500;
    // Assign optional fields only when present — required by exactOptionalPropertyTypes
    if (opts.details !== undefined) this.details = opts.details;
    if (opts.cause !== undefined) this.cause = opts.cause;
  }
}

// ---------------------------------------------------------------------------
// Error code families (§21.1)
// ---------------------------------------------------------------------------
export const ErrorCodes = {
  // Auth — 401
  AUTH_MISSING: "AUTH_MISSING",
  AUTH_INVALID: "AUTH_INVALID",
  AUTH_EXPIRED: "AUTH_EXPIRED",
  // Access — 403
  ACCESS_DENIED: "ACCESS_DENIED",
  ACCESS_STORE_MISMATCH: "ACCESS_STORE_MISMATCH",
  WIDGET_GLOBALLY_DISABLED: "WIDGET_GLOBALLY_DISABLED",
  WIDGET_ENFORCEMENT_UNAVAILABLE: "WIDGET_ENFORCEMENT_UNAVAILABLE",
  // Store
  STORE_NOT_FOUND: "STORE_NOT_FOUND",
  STORE_SUSPENDED: "STORE_SUSPENDED",
  STORE_NOT_READY: "STORE_NOT_READY",
  // Platform
  PLATFORM_REAUTHORIZATION_REQUIRED: "PLATFORM_REAUTHORIZATION_REQUIRED",
  // Shopify
  SHOPIFY_API_ERROR: "SHOPIFY_API_ERROR",
  SHOPIFY_REAUTHORIZATION_REQUIRED: "SHOPIFY_REAUTHORIZATION_REQUIRED",
  SHOPIFY_TOKEN_EXCHANGE_FAILED: "SHOPIFY_TOKEN_EXCHANGE_FAILED",
  SHOPIFY_TOKEN_EXPIRED: "SHOPIFY_TOKEN_EXPIRED",
  SHOPIFY_SCOPE_CHANGED: "SHOPIFY_SCOPE_CHANGED",
  // Validation — 400
  VALIDATION_INVALID_BODY: "VALIDATION_INVALID_BODY",
  VALIDATION_INVALID_PARAM: "VALIDATION_INVALID_PARAM",
  VALIDATION_MISSING_FIELD: "VALIDATION_MISSING_FIELD",
  // Merchant team
  MERCHANT_TEAM_LAST_FULL_ACCESS_USER: "MERCHANT_TEAM_LAST_FULL_ACCESS_USER",
  // Product
  PRODUCT_NOT_FOUND: "PRODUCT_NOT_FOUND",
  PRODUCT_NOT_ELIGIBLE: "PRODUCT_NOT_ELIGIBLE",
  // Competitor
  COMPETITOR_NOT_FOUND: "COMPETITOR_NOT_FOUND",
  COMPETITOR_DISABLED: "COMPETITOR_DISABLED",
  // Policy
  POLICY_NOT_APPLICABLE: "POLICY_NOT_APPLICABLE",
  // Mapping
  MAPPING_NOT_FOUND: "MAPPING_NOT_FOUND",
  MAPPING_STALE: "MAPPING_STALE",
  // Request
  FEEDBACK_NOT_FOUND: "FEEDBACK_NOT_FOUND",
  REQUEST_NOT_FOUND: "REQUEST_NOT_FOUND",
  REQUEST_EXPIRED: "REQUEST_EXPIRED",
  REQUEST_DUPLICATE: "REQUEST_DUPLICATE",
  REQUEST_INVALID_STATUS: "REQUEST_INVALID_STATUS",
  REQUEST_STATE_CONFLICT: "REQUEST_STATE_CONFLICT",
  REQUEST_CUSTOMER_LINK_STALE: "REQUEST_CUSTOMER_LINK_STALE",
  // Extraction
  EXTRACTION_FAILED: "EXTRACTION_FAILED",
  EXTRACTION_UNSUPPORTED: "EXTRACTION_UNSUPPORTED",
  // Verification
  VERIFICATION_FAILED: "VERIFICATION_FAILED",
  VERIFICATION_UNVERIFIABLE: "VERIFICATION_UNVERIFIABLE",
  // Offer
  OFFER_NOT_FOUND: "OFFER_NOT_FOUND",
  OFFER_EXPIRED: "OFFER_EXPIRED",
  OFFER_ALREADY_ACCEPTED: "OFFER_ALREADY_ACCEPTED",
  // Monitoring
  MONITORING_NOT_CONFIGURED: "MONITORING_NOT_CONFIGURED",
  BILLING_SUBSCRIPTION_INACTIVE: "BILLING_SUBSCRIPTION_INACTIVE",
  BILLING_ALLOWANCE_EXHAUSTED: "BILLING_ALLOWANCE_EXHAUSTED",
  BILLING_VERIFICATION_UNAVAILABLE: "BILLING_VERIFICATION_UNAVAILABLE",
  BILLING_CONFIGURATION_INVALID: "BILLING_CONFIGURATION_INVALID",
  // Notification
  NOTIFICATION_NOT_FOUND: "NOTIFICATION_NOT_FOUND",
  // Admin
  ADMIN_NOT_FOUND: "ADMIN_NOT_FOUND",
  ADMIN_ACTION_INVALID: "ADMIN_ACTION_INVALID",
  ADMIN_AUTH_CONFIG_MISSING: "ADMIN_AUTH_CONFIG_MISSING",
  ADMIN_AUTH_DENIED: "ADMIN_AUTH_DENIED",
  ADMIN_DOMAIN_NOT_ALLOWED: "ADMIN_DOMAIN_NOT_ALLOWED",
  ADMIN_REASON_REQUIRED: "ADMIN_REASON_REQUIRED",
  ADMIN_STALE_CURSOR: "ADMIN_STALE_CURSOR",
  ADMIN_SUPPORT_ACCESS_REQUIRED: "ADMIN_SUPPORT_ACCESS_REQUIRED",
  GLOBAL_WIDGET_SETTING_CONFLICT: "GLOBAL_WIDGET_SETTING_CONFLICT",
  MANUAL_FIXTURE_SETTING_CONFLICT: "MANUAL_FIXTURE_SETTING_CONFLICT",
  // Rate limiting — 429
  RATE_LIMIT_EXCEEDED: "RATE_LIMIT_EXCEEDED",
  // Internal — 500
  INTERNAL_ERROR: "INTERNAL_ERROR",
  INTERNAL_TIMEOUT: "INTERNAL_TIMEOUT",
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

/** Map error code families to HTTP status codes. */
export function httpStatusForCode(code: string): number {
  if (code.startsWith("AUTH_")) return 401;
  if (code.startsWith("ACCESS_")) return 403;
  if (code.startsWith("VALIDATION_")) return 400;
  if (code === "REQUEST_CUSTOMER_LINK_STALE") return 409;
  if (code === "ADMIN_STALE_CURSOR") return 400;
  if (code.startsWith("BILLING_ALLOWANCE_") || code === "BILLING_SUBSCRIPTION_INACTIVE") return 409;
  if (code === "BILLING_VERIFICATION_UNAVAILABLE") return 503;
  if (code === "BILLING_CONFIGURATION_INVALID") return 500;
  if (code.startsWith("RATE_LIMIT_")) return 429;
  if (code.endsWith("_NOT_FOUND")) return 404;
  if (code.startsWith("INTERNAL_")) return 500;
  return 500;
}

// ---------------------------------------------------------------------------
// Public response envelopes (§21.1)
// ---------------------------------------------------------------------------

/** Error response body. Stack traces and internal cause MUST NOT appear here. */
export interface ErrorEnvelope {
  error: {
    code: string;
    message: string;
    requestId: string;
    retryable: boolean;
    details: Record<string, unknown> | null;
  };
}

/** Paginated list response body. */
export interface ListEnvelope<T> {
  items: T[];
  nextCursor: string | null;
  meta: { requestId: string };
}

/** Reusable cursor pagination metadata for component-ready list responses. */
export interface ListPaginationDto {
  nextCursor: string | null;
  currentCursor: string | null;
  staleCursor: boolean;
}

/**
 * Build a safe public error envelope.
 * Deliberately excludes `cause`, stack traces, and raw internal details.
 */
export function buildErrorEnvelope(
  error: Pick<ApplicationError, "code" | "message" | "retryable" | "details">,
  requestId: string,
): ErrorEnvelope {
  return {
    error: {
      code: error.code,
      message: error.message,
      requestId,
      retryable: error.retryable,
      details: error.details ?? null,
    },
  };
}

export function buildListEnvelope<T>(
  items: T[],
  nextCursor: string | null,
  requestId: string,
): ListEnvelope<T> {
  return { items, nextCursor, meta: { requestId } };
}
